import { useEffect, useState } from "react";
import type { OwnerTerritory } from "../../domain/owner-territories";
import { isIdentityEligible } from "../../domain/grid-power";
import { validateDisplayName } from "../../domain/territory-identity";
import { useTerritoryIdentity } from "./useTerritoryIdentity";
import { BrandImageSection } from "./BrandImageSection";
import { TerritoryReconciliation } from "./TerritoryReconciliation";
import { OwnerModerationBanner } from "../moderation/OwnerModerationBanner";
import { useI18n } from "../../i18n";
import { cn } from "../cn";

const SAVE_ERROR_CODES = new Set([
  "STALE_TERRITORY",
  "IDENTITY_VERSION_CONFLICT",
  "IDENTITY_RECONCILIATION_REQUIRED",
  "TERRITORY_NOT_PUBLISHABLE",
  "TERRITORY_NOT_IDENTITY_ELIGIBLE",
  "DISPLAY_NAME_INVALID",
  "UNTRUSTED_ORIGIN",
  "CSRF_REQUIRED",
  "CSRF_INVALID",
  "CSRF_FETCH_FAILED",
  "UNAUTHENTICATED",
  "UNSUPPORTED_MEDIA_TYPE",
  "SEED_NOT_OWNED",
]);

/**
 * WO#4B — Territory Identity management (V1: Display Name only).
 * Real backend persistence. No fake optimistic state, no fake IDs, no disabled
 * future features, no Brand Image / Canvas / media.
 */
export function TerritoryIdentity({ territory }: { territory: OwnerTerritory }) {
  const { t } = useI18n();
  const seedParcelId = territory.unitIds.length > 0 ? territory.unitIds[0] : null;
  const { state, saving, saveError, save, reconciling, reconcileError, reconcile } =
    useTerritoryIdentity(seedParcelId, isIdentityEligible(territory.area));

  const [name, setName] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // Sync the input to the authoritative persisted value whenever it changes.
  useEffect(() => {
    if (state.status === "ready") {
      setName(state.identity ? state.identity.display_name : "");
    }
  }, [state]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setJustSaved(false);
    const v = validateDisplayName(name);
    if (!v.ok) {
      setLocalError(v.reason);
      return;
    }
    const ok = await save(v.value);
    if (ok) setJustSaved(true);
  };

  const saveErrorText = saveError
    ? SAVE_ERROR_CODES.has(saveError.code)
      ? t(`dashboard.identity.saveErrors.${saveError.code}`)
      : saveError.message
    : null;

  return (
    <section
      data-testid="territory-identity"
      className="mt-6 rounded-lg bg-panel border border-border-soft p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <h3 className="m-0 shrink-0 font-display text-[14px] font-semibold tracking-[-0.01em] text-text-primary">
            {t("dashboard.identity.title")}
          </h3>
          {state.status === "ready" && state.identity?.display_name && (
            <>
              <span aria-hidden className="shrink-0 text-text-muted">·</span>
              <span
                data-testid="territory-identity-current-name"
                title={state.identity.display_name}
                className="min-w-0 truncate font-mono text-[11px] text-neon-teal"
              >
                {state.identity.display_name}
              </span>
            </>
          )}
        </div>
        {state.status === "ready" && state.identity && (
          <span
            data-testid="territory-identity-revision"
            className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted"
          >
            {t("dashboard.identity.rev", { n: state.identity.revision })}
          </span>
        )}
      </div>

      {state.status === "ready" && state.identity && (
        <OwnerModerationBanner
          targetType="territory"
          targetId={state.identity.id}
          className="mt-4"
        />
      )}

      {state.status === "auth_required" && (
        <p data-testid="territory-identity-auth-required" className="mt-4 text-[12px] text-text-muted">
          {t("dashboard.identity.authRequired")}
        </p>
      )}

      {state.status === "unavailable" && (
        <p data-testid="territory-identity-unavailable" className="mt-4 text-[12px] text-text-muted">
          {t("dashboard.identity.unavailable")}
        </p>
      )}

      {state.status === "loading" && (
        <p data-testid="territory-identity-loading" className="mt-4 text-[12px] text-text-muted font-mono">
          {t("dashboard.identity.loading")}
        </p>
      )}

      {state.status === "error" && (
        <p data-testid="territory-identity-error" className="mt-4 text-[12px] text-state-danger">
          {state.message}
        </p>
      )}

      {state.status === "reconciliation_required" && (
        <TerritoryReconciliation
          candidates={state.candidates}
          reconciling={reconciling}
          reconcileError={reconcileError}
          onConfirm={reconcile}
        />
      )}

      {state.status === "ready" && (
        <form className="mt-4" onSubmit={onSubmit}>
          <input
            id="td-identity-name"
            data-testid="territory-identity-name-input"
            type="text"
            value={name}
            maxLength={64}
            onChange={(e) => {
              setName(e.target.value);
              setLocalError(null);
              setJustSaved(false);
            }}
            placeholder={t("dashboard.identity.namePlaceholder")}
            className={cn(
              "w-full h-10 px-3 rounded-md bg-obsidian border border-border-soft",
              "font-mono text-[13px] text-text-primary outline-none",
              "focus:border-border-accent",
            )}
          />

          {localError && (
            <p data-testid="territory-identity-local-error" className="mt-2 text-[11px] text-state-danger">
              {localError}
            </p>
          )}
          {saveError && (
            <p data-testid="territory-identity-save-error" className="mt-2 text-[11px] text-state-danger">
              {saveErrorText}
            </p>
          )}
          {justSaved && !saveError && (
            <p data-testid="territory-identity-saved" className="mt-2 text-[11px] text-neon-teal">
              {t("dashboard.identity.saved")}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <button
              type="submit"
              data-testid="territory-identity-save"
              disabled={saving}
              className={cn(
                "inline-flex items-center gap-2 h-9 px-5",
                "font-mono text-[11px] font-medium tracking-[0.16em] uppercase",
                "text-text-primary bg-transparent border border-border-strong rounded-md hover:bg-white/5",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {saving ? t("dashboard.identity.saving") : t("dashboard.identity.saveName")}
            </button>
            {state.identity && (
              <a
                data-testid="territory-edit-canvas"
                href={`#canvas/${state.identity.id}`}
                className={cn(
                  "inline-flex items-center gap-2 h-9 px-5 no-underline",
                  "font-mono text-[11px] font-medium tracking-[0.16em] uppercase",
                  "text-neon-teal bg-transparent border border-border-accent rounded-md hover:bg-neon-teal-soft",
                )}
              >
                {t("dashboard.identity.editCanvas")}
              </a>
            )}
          </div>
        </form>
      )}

      {state.status === "ready" && state.identity && (
        <BrandImageSection
          identityId={state.identity.id}
          hasAuthority={true}
          territoryWidth={territory.bounds ? territory.bounds.maxX - territory.bounds.minX : undefined}
          territoryHeight={territory.bounds ? territory.bounds.maxY - territory.bounds.minY : undefined}
        />
      )}
    </section>
  );
}
